import { qs } from '@weapp/utils';
import { IAnalyzeOption, IEbdappLog, IEbdappLogInput } from './types';

class Log {
  // 最大长度
  maxLen?: number;

  // 到达最大日志时是否不删除前置日志，默认删除
  isNotShiftLog?: boolean;

  // dom监控列表
  observedDom: Element[] = [];

  // 传入最大长度作为初始值
  constructor(maxLen?: number, isNotShiftLog?: boolean) {
    this.maxLen = maxLen;
    this.isNotShiftLog = isNotShiftLog;
  }

  // 日志列表
  list: IEbdappLog[] = [];

  // 设置一条日志
  set = (log: IEbdappLogInput) => {
    const logType = typeof log === 'object' && log.logType;
    if (logType) {
      if (this.isNotShiftLog && this.maxLen && this.list.length >= this.maxLen) {
        return;
      }
      // 日志会默认添加时间戳，后续分析使用
      this.list.push({ ...log, time: performance.now() });
      // 日志只保存固定最大长度的日志列表，多余部分会被剔除
      if (this.maxLen) {
        if (this.list.length > this.maxLen) {
          this.list.shift();
        }
      }
      // 监听dom
      const ebdpageDom = document.querySelector('.ebpage');
      if (ebdpageDom) {
        if (ebdpageDom.getAttribute('observe') !== 'true') {
          const observer = new MutationObserver((mutations) => {
            // 只处理添加eb组件的突变
            const allowedMutations = mutations.filter((_mu: any) => _mu.addedNodes[0]?.classList?.contains('ebcom'));
            if (!allowedMutations.length) return;
            const ebcoms = document.querySelectorAll('.ebcom');
            ebcoms.forEach((comDom) => {
              const domId = comDom.id;
              if (domId && !this.observedDom.includes(comDom)) {
                const header = comDom.querySelector('.header')?.textContent;
                this.observedDom.push(comDom);
                const domObserver = new MutationObserver((_mutations) => {
                  console.log(_mutations);
                  // 如果是添加子组件的突变，需要过滤掉日志相关的
                  const _allowedMutations = _mutations.filter(
                    (_mu: any) => (_mu.type === 'childList'
                        && !_mu.target?.classList?.contains('log_com_showTime')
                        && !_mu.addedNodes[0]?.classList?.contains('log_com_showTime'))
                      || _mu.type !== 'childList',
                  );
                  if (!_allowedMutations.length) return;
                  // 渲染出走马灯时取消监听
                  if (comDom.querySelector('.ui-carousel')) {
                    domObserver.disconnect();
                    return;
                  }
                  this.set({
                    logType: 'designer-render-ebcom-change',
                    data: {
                      comId: domId,
                      name: header || domId,
                    },
                  });
                });
                domObserver.observe(comDom, {
                  subtree: true,
                  childList: true,
                  attributes: true,
                  characterData: true,
                });
              }
            });
          });
          ebdpageDom.setAttribute('observe', 'true');
          observer.observe(ebdpageDom, {
            subtree: true,
            childList: true,
          });
        }
      }
    }
  };

  analyze = (opt?: IAnalyzeOption) => {
    // 分析列表
    const analyzeList = this.list.slice();
    // 关键日志时间
    const keyLogTimes = {
      initSourceCode: -1,
      designerRender: -1,
      // entrance接口开始请求
      entranceApiStart: -1,
      // entrance缓存开始请求
      entranceCacheStart: -1,
      // 白屏开始
      whiteStart: -1,
      // loading开始
      loadingStart: -1,
      // 获取支持付费控制的模块列表开始
      getPaySupportModulesStart: -1,
      // 组件最后修改
      lastComsChange: -1,
      // 白屏结束
      whiteEnd: -1,
      // ebdpage-view开始加载
      ebdpageViewLoadStart: -1,
      // 组件隐藏时间节点
      comsHiddenTime: {},
      // eb组件隐藏到显示时间节点
      comsShowTime: {} as any,
      // 组件加载完成时间
      comsChangeTime: {} as any,
      // 页面组件开始渲染到首屏渲染完成
      firstScreenLoaded: -1,
      // 进入ebdapp库的时间
      ebdappStartTime: -1,
      // ebdapp加载时间
      ebdappLibLoadTime: -1,
      // ebdapp运行时间
      ebdappLibRunTime: -1,
    };
    // 分析结果
    const analysisResult = {
      // 进入ebdpage库的时间
      ebdpageStartTime: -1,
      // loading时间
      loadingTime: -1,
      // 获取ecode数据
      ecodeLoadTime: -1,
      // 从加载我们库到开始渲染
      loadToRender: -1,
      // loading结束后白屏时间
      whiteAfterLoading: -1,
      // 渲染耗时
      renderTime: -1,
      // 渲染完毕时间
      renderCompleteTime: -1,
      // 首屏渲染完成事件
      firstScreenDomLoaded: -1,
      // 最外层容器渲染时间
      outermostContainer: -1,
      // entrance接口时间
      entranceApiTime: -1,
      // entrance缓存时间
      entranceCacheTime: -1,
      // 获取支持付费控制的模块列表
      getPaySupportModules: -1,
      // 组件渲染信息
      comLoadedInfo: '',
      // ebdpage加载时间
      ebdpageLibLoadTime: -1,
      // ebdpage-view加载时间
      ebdpageViewLoadTime: -1,
      // ebdpage运行时间
      ebdpageLibRunTime: -1,
      // entrance接口CostTime序列化文本
      entranceCostTime: '',
      // eb组件外层容器渲染次数
      comsLayoutRenderCount: -1,
    };
    // 资源日志
    const resourceLog: any[] = [];
    // 请求栈
    const requestStack: any[][] = [[]];
    analyzeList.forEach((logData) => {
      const { logType, time, data } = logData || {};
      if (logType === 'ebdapp-routeMain-render') {
        keyLogTimes.ebdappStartTime = time;
        return;
      }
      if (logType === 'ebdpage-route-page-render' && analysisResult.ebdpageStartTime < 0) {
        analysisResult.ebdpageStartTime = time;
        return;
      }
      if (logType === 'load-page-view') {
        keyLogTimes.ebdpageViewLoadStart = time;
      }
      if (logType === 'PageView-init') {
        keyLogTimes.loadingStart = time;
        analysisResult.ebdpageViewLoadTime = time - keyLogTimes.ebdpageViewLoadStart;
        if (analysisResult.ebdpageStartTime > 0) {
          analysisResult.ebdpageLibRunTime = time - analysisResult.ebdpageStartTime;
        }
        return;
      }
      if (logType === 'designer-render-loading-off') {
        keyLogTimes.whiteStart = time;
        analysisResult.loadingTime = time - keyLogTimes.loadingStart;
        return;
      }
      if (logType === 'getPaySupportModules-start') {
        keyLogTimes.getPaySupportModulesStart = time;
        return;
      }
      if (logType === 'getPaySupportModules-end') {
        analysisResult.getPaySupportModules = time - keyLogTimes.getPaySupportModulesStart;
        return;
      }
      if (logType === 'getCacheDatas-start') {
        keyLogTimes.entranceCacheStart = time;
        return;
      }
      if (logType === 'initLoadData-start') {
        analysisResult.entranceCacheTime = time - keyLogTimes.entranceCacheStart;
        return;
      }
      if (logType === 'entrance-start') {
        keyLogTimes.entranceApiStart = time;
        return;
      }
      if (logType === 'entrance-end') {
        if (data) {
          let logString = '';
          Object.keys(data).forEach((key) => {
            if (Array.isArray(data[key])) {
              data[key].forEach((_sdata: any, _index: number) => {
                Object.keys(_sdata).forEach((skey) => {
                  logString += `\n${key}-${_index}-${skey}:${_sdata[skey]}`;
                });
              });
            } else {
              logString += `\n${key}:${data[key]}`;
            }
          });
          analysisResult.entranceCostTime = logString;
        }
        analysisResult.entranceApiTime = time - keyLogTimes.entranceApiStart;
        return;
      }
      if (logType === 'init-source-code') {
        keyLogTimes.initSourceCode = time;
        return;
      }
      if (logType === 'load-ecode-success') {
        analysisResult.ecodeLoadTime = time - keyLogTimes.initSourceCode;
        return;
      }
      if (logType === 'designer-render-grid-render') {
        keyLogTimes.designerRender = time;
        analysisResult.loadToRender = time - analysisResult.ebdpageStartTime;
        return;
      }
      if (logType === 'first-screen-loaded') {
        keyLogTimes.firstScreenLoaded = time - keyLogTimes.designerRender;
        return;
      }
      if (logType === 'designer-render-ebcom-hidden') {
        keyLogTimes.comsHiddenTime = {
          ...keyLogTimes.comsHiddenTime,
          [data!.comId as any]: time,
        };
        return;
      }
      if (logType === 'designer-render-ebcom-show') {
        if (analysisResult.whiteAfterLoading < 0) {
          analysisResult.whiteAfterLoading = time - keyLogTimes.whiteStart;
        }
        keyLogTimes.comsShowTime = {
          ...keyLogTimes.comsShowTime,
          [data!.comId as any]: {
            time,
            name: data!.name,
            comId: data!.comId,
          },
        };
        if (keyLogTimes.whiteEnd < 0) {
          keyLogTimes.whiteEnd = time;
        }
        return;
      }
      if (logType === 'designer-render-ebcom-change') {
        if (!data?.comId) {
          console.error(data);
          return;
        }
        keyLogTimes.comsChangeTime[data!.comId as any] = {
          firstTime: keyLogTimes.comsChangeTime[data!.comId as any]?.firstTime || time,
          time,
          name: data!.name,
          comId: data!.comId,
        };
        keyLogTimes.lastComsChange = time;
        return;
      }

      if (logType === 'designer-render-grid-layout-coms-layout') {
        analysisResult.comsLayoutRenderCount = (logData!.data as any) || -1;
      }
    });
    // 分析资源加载情况
    const resourceEntries = performance.getEntriesByType('resource');

    resourceEntries.forEach((entry) => {
      const {
        duration,
        startTime,
        responseEnd,
        name, // initiatorType,
      } = entry as PerformanceResourceTiming;

      const findStack = requestStack.find((stack) => {
        if (stack.length) {
          if (stack[stack.length - 1].responseEnd <= startTime) {
            stack.push(entry);
            return true;
          }
        } else {
          stack.push(entry);
          return true;
        }
        return false;
      });
      if (!findStack) {
        requestStack.push([entry]);
      }

      if (
        name.indexOf('build/ebdapp/static/js/') > -1
        || name.indexOf('build/ebdpage/static/js/') > -1
        || name.indexOf('build/designer/static/js/') > -1
        || name.indexOf('build/ebdnavcoms/static/') > -1
        || name.indexOf('build/ebdcoms/static/') > -1
        || name.indexOf('build/ebddatacoms/static/') > -1
        || name.indexOf('build/ebdcontainercoms/static/') > -1
        || name.indexOf('build/ebdothercoms/static/') > -1
      ) {
        resourceLog.push({
          duration,
          startTime,
          responseEnd,
          name,
        });
        if (name.includes('build/ebdapp/static/js/lib.js')) {
          keyLogTimes.ebdappLibLoadTime = duration;
        }
        if (name.includes('build/ebdpage/static/js/lib.js')) {
          analysisResult.ebdpageLibLoadTime = duration;
          if (keyLogTimes.ebdappStartTime > 0) {
            keyLogTimes.ebdappLibRunTime = startTime - keyLogTimes.ebdappStartTime;
          }
        }
      }
    });
    analysisResult.renderTime = keyLogTimes.lastComsChange - keyLogTimes.whiteEnd;
    analysisResult.renderCompleteTime = keyLogTimes.lastComsChange;
    const formatTime = (_time: number | string) => {
      if (typeof _time === 'string') {
        return _time;
      }
      return _time > -1 ? Number(_time.toFixed(2)) : _time;
    };

    analysisResult.comLoadedInfo = Object.keys(keyLogTimes.comsChangeTime)
      .map((comId) => {
        const finishTime = keyLogTimes.comsChangeTime[comId];
        const startTime = keyLogTimes.comsShowTime[comId];
        let comShowTimeDom = opt?.showDomLog
          ? (document.querySelector(`#${comId} > .log_com_showTime`) as HTMLSpanElement | null)
          : null;
        if (!comShowTimeDom && opt?.showDomLog) {
          comShowTimeDom = document.createElement('span');
          comShowTimeDom.classList.add('log_com_showTime');
          comShowTimeDom.style.cssText = 'background-color:#ff000069;';
          document.querySelector(`#${comId}`)?.append(comShowTimeDom);
        }
        if (finishTime && startTime) {
          const renderTime = finishTime.time - startTime.time;
          const renderSkelectonTime = finishTime.firstTime - startTime.time;
          if (comId === opt?.tableData?.firstScreenLastId) {
            analysisResult.firstScreenDomLoaded = finishTime.time;
          }
          if (comId === opt?.tableData?.outermostContainerId) {
            analysisResult.outermostContainer = renderSkelectonTime;
          }
          if (comShowTimeDom) {
            comShowTimeDom.innerHTML = `渲染骨架用时${renderSkelectonTime}ms，渲染完成用时${renderTime}ms，渲染完成时间${finishTime.time}ms`;
          }
          return `${startTime.name}(${comId}):渲染骨架用时${renderSkelectonTime}ms，渲染完成用时${renderTime}ms，渲染完成时间${finishTime.time}ms\n`;
        }
        if (comShowTimeDom) {
          comShowTimeDom.innerHTML = `${comId}`;
        }
        return `${startTime?.name || finishTime?.name}(${comId})\n`;
      })
      .join('');
    Object.keys(analysisResult).forEach((_key) => {
      (analysisResult as any)[_key] = formatTime((analysisResult as any)[_key]);
    });
    if (opt?.tableData) {
      const preData = localStorage.getItem('LOGTABLEDATA');
      const tableData = [...(preData ? JSON.parse(preData) : []), analysisResult];
      if (opt.tableData?.show) {
        console.table(tableData);
      }
      if (opt.tableData?.clean) {
        localStorage.removeItem('LOGTABLEDATA');
      } else {
        localStorage.setItem('LOGTABLEDATA', JSON.stringify(tableData));
      }
    }
  };
}
export type EbdappLogType = Log;
// 页面参数带有logLength才会启动日志，减少内存开销，logLength会作为入参作为日志的最大长度
const { search: locationSearch } = location;
const searchParams = qs.parse(locationSearch?.slice(1));
const { logLength, showFirstLog } = searchParams;
if (logLength && !window.EBDAPPLOG) {
  window.EBDAPPLOG = new Log(Number(logLength) || 100, !!showFirstLog);
}
export const LOG = window.EBDAPPLOG as EbdappLogType;
export default {};
